'use client'

import { schoolExpiryNoticeMessage } from '@/lib/school/access-after-expiry'

export type SchoolExpiryBannerData = {
  schoolName: string
  daysLeft: number
  lastDayLabel: string
  urgent: boolean
}

/**
 * "Your access from Lincoln High ends soon: Oct 14, 2026 is the last day" —
 * shown site-wide over the last two weeks of a school license, to the school's
 * students. Mirrors CompExpiryBanner: not dismissible (there is a deadline),
 * amber, never red (nothing is their fault), and it says up front that their
 * work stays saved.
 *
 * The action is the explanation, not a purchase: a school student cannot buy
 * while the license runs (SCHOOL_COVERED), and the likeliest outcome is that
 * the school renews. /settings#subscription spells out what changes.
 */
export default function SchoolExpiryBanner({ data }: { data: SchoolExpiryBannerData | null }) {
  if (!data) return null
  return (
    <div
      role="status"
      data-testid="school-expiry-banner"
      className={
        data.urgent
          ? 'w-full border-b border-amber-400 bg-amber-100 text-amber-950 dark:border-amber-800/60 dark:bg-amber-950/50 dark:text-amber-100'
          : 'w-full border-b border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-100'
      }
    >
      <div className="mx-auto flex w-full max-w-[1100px] flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2 text-sm">
        <span className="font-medium">{schoolExpiryNoticeMessage(data)}</span>
        <span className="opacity-80">Your scores and progress stay saved.</span>
        <a
          href="/settings#subscription"
          className="ml-auto rounded-md bg-foreground px-3 py-1 text-xs font-semibold text-background"
        >
          What changes
        </a>
      </div>
    </div>
  )
}
